//@flow
import React from 'react';
import {makeStyles, Tooltip, Typography} from '@material-ui/core';
import {Check, HelpOutline, InfoOutlined} from '@material-ui/icons';

import {colors} from '../../../assets/styles/colors';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import Collapsible from '../../../elements/Collapsible/Collapsible';
import type {Node} from 'react';

export type Props = {
  active?: boolean,
  label: string,
  required?: boolean,
  to: string,
  description: string,
  id: string,
  selected?: boolean,
  onClick?: (id: string) => void,
  info?: string,
  disabled?: boolean,
  disabledInfo?: string,
  externalLink?: boolean,
  isButton?: boolean,
  component?: Node,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '.5rem',
    borderRadius: '8px',
    background: colors.grey100,
    border: `1px dashed ${colors.grey300}`,
    textDecoration: 'none',
    color: colors.grey900,
    textTransform: 'none',
    position: 'relative',

    '&[data-active="true"]': {
      background: '#E8FCDA !important',
    },

    '&[data-disabled="true"]': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },

    '& .MuiButton-label': {
      display: 'flex',
      justifyContent: 'flex-start',
      gap: theme.spacing(2),
    },

    '&[data-required="true"]': {
      background: colors.grey200,
      boxShadow:
        'inset 0px 0px 1px rgba(40, 41, 61, 0.08), inset 0px 0.5px 2px rgba(96, 97, 112, 0.16);',
      border: 'none',
    },
    '&[data-selected="true"]': {
      background: '#D1FFB180',
    },

    [theme.breakpoints.up('sm')]: {
      padding: '.75rem',
    },
    [theme.breakpoints.up('lg')]: {
      padding: '1rem',
    },
  },
  data: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    maxWidth: '100%',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    minWidth: '20px',
    minHeight: '20px',
    background: colors.grey400,
    boxShadow: 'inset 1px 2px 4px rgba(0, 0, 0, 0.15)',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&[data-active="true"]': {
      background: 'linear-gradient(200.22deg, #02DB9A 9.9%, #00B382 88.75%)',
      color: '#FFF',
    },

    [theme.breakpoints.up('sm')]: {
      width: '24px',
      height: '24px',
      minWidth: '24px',
      minHeight: '24px',
    },
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: colors.grey900,
    textAlign: 'start',
    // [theme.breakpoints.up('md')]: {
    //   flexGrow: 1,
    // },
  },
  description: {
    fontSize: '12px',
    fontWeight: 400,
    maxWidth: '100%',
    color: colors.grey700,
    textAlign: 'start',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // whiteSpace: 'nowrap',

    // [theme.breakpoints.up('md')]: {
    //   flexGrow: 1,
    //   maxWidth: '80%',
    // },
  },
  infoButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '.25rem',
    color: colors.grey600,
    position: 'absolute',
    right: '.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  tooltip: {
    backgroundColor: colors.grey200,
    padding: '.5rem',
    borderRadius: '8px',
    maxWidth: '300px',
    color: colors.grey900,

    '& .MuiTooltip-arrow': {
      color: colors.grey200,
    },
  },

  customCollapse: {
    marginTop: '0!important',
    backgroundColor: `#F4F4F6!important`,
    borderRadius: 8,
  },
  inlineHeader: {
    display: 'flex',
    gap: '8px',
    width: '100%',
  },
  activeCollapse: {
    background: '#E4FCD6!important',
  },
}));

type OnboardingItemExpansibleProps = {
  title: Node,
  children?: Node,
  className?: string,
};

export const OnboardingItemExpansible = ({
  title,
  children,
  className,
}: OnboardingItemExpansibleProps) => {
  const classes = useStyles();
  return (
    <Collapsible
      title={title}
      className={`${classes.customCollapse} ${className ?? ''}`}
      divisible={false}
      startClosed
    >
      {children}
    </Collapsible>
  );
};

export default function OnboardingItem(props: Props) {
  const {
    active,
    label,
    description,
    required,
    to,
    disabledInfo,
    selected,
    info,
    disabled,
    externalLink,
    onClick,
    isButton,
    component,
  } = props;
  const classes = useStyles();

  const url = externalLink ? to : `${to}?fromOnboarding=true`;

  const handleClick = (e: Event) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (onClick) {
      onClick(props.id);
    }
  };

  if (isButton) {
    return (
      <CustomButton
        className={classes.root}
        data-selected={selected}
        data-required={required}
        data-active={active}
        data-disabled={disabled}
        onClick={handleClick}
        endIcon={
          disabledInfo &&
          disabled && (
            <Tooltip
              title={disabledInfo}
              placement="left"
              classes={{tooltip: classes.tooltip}}
            >
              <span className={classes.infoButton}>
                <HelpOutline />
              </span>
            </Tooltip>
          )
        }
      >
        {required && (
          <span className={classes.checkbox} data-active={active}>
            {active && <Check fontSize="small" />}
          </span>
        )}
        <div className={classes.data}>
          <Typography className={classes.label}>{label}</Typography>
          <Typography className={classes.description}>{description}</Typography>
        </div>
        {info && !disabled && (
          <Tooltip
            title={info}
            placement="left"
            classes={{tooltip: classes.tooltip}}
          >
            <span className={classes.infoButton}>
              <InfoOutlined />
            </span>
          </Tooltip>
        )}
      </CustomButton>
    );
  }

  if (externalLink) {
    return (
      <CustomButton
        href={url}
        className={classes.root}
        data-selected={selected}
        data-required={required}
        data-active={active}
        data-disabled={disabled}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        endIcon={
          disabledInfo &&
          disabled && (
            <Tooltip
              title={disabledInfo}
              placement="left"
              classes={{tooltip: classes.tooltip}}
            >
              <span className={classes.infoButton}>
                <HelpOutline />
              </span>
            </Tooltip>
          )
        }
      >
        {required && (
          <span className={classes.checkbox} data-active={active}>
            {active && <Check fontSize="small" />}
          </span>
        )}
        <div className={classes.data}>
          <Typography className={classes.label}>{label}</Typography>
          <Typography className={classes.description}>{description}</Typography>
        </div>
        {info && !disabled && (
          <Tooltip
            title={info}
            placement="left"
            classes={{tooltip: classes.tooltip}}
          >
            <span className={classes.infoButton}>
              <InfoOutlined />
            </span>
          </Tooltip>
        )}
      </CustomButton>
    );
  }

  return (
    <OnboardingItemExpansible
      className={`${active ? classes.activeCollapse : ''}`}
      title={
        <div className={classes.inlineHeader}>
          {required && (
            <span className={classes.checkbox} data-active={active}>
              {active && <Check fontSize="small" />}
            </span>
          )}
          <div className={classes.data}>
            <Typography className={classes.label}>{label}</Typography>
            <Typography className={classes.description}>
              {description}
            </Typography>
          </div>
        </div>
      }
    >
      {component}
    </OnboardingItemExpansible>
  );

  // return (
  //   <CustomButton
  //     to={url}
  //     className={classes.root}
  //     data-selected={selected}
  //     data-required={required}
  //     data-active={active}
  //     data-disabled={disabled}
  //     target={externalLink ? '_blank' : '_self'}
  //     rel={externalLink ? 'noopener noreferrer' : ''}
  //     onClick={handleClick}
  //     component={Link}
  //     endIcon={
  //       disabledInfo &&
  //       disabled && (
  //         <Tooltip
  //           title={disabledInfo}
  //           placement="left"
  //           classes={{tooltip: classes.tooltip}}
  //         >
  //           <span className={classes.infoButton}>
  //             <HelpOutline />
  //           </span>
  //         </Tooltip>
  //       )
  //     }
  //   >
  //     {required && (
  //       <span className={classes.checkbox} data-active={active}>
  //         {active && <Check fontSize="small" />}
  //       </span>
  //     )}
  //     <div className={classes.data}>
  //       <Typography className={classes.label}>{label}</Typography>
  //       <Typography className={classes.description}>{description}</Typography>
  //     </div>
  //     {info && !disabled && (
  //       <Tooltip
  //         title={info}
  //         placement="left"
  //         classes={{tooltip: classes.tooltip}}
  //       >
  //         <span className={classes.infoButton}>
  //           <InfoOutlined />
  //         </span>
  //       </Tooltip>
  //     )}

  //     {/* <ChevronRight /> */}
  //   </CustomButton>
  // );
}
